import { alpha } from "@mui/material/styles";
import type { TPeriod } from "store/periodStore";
import { PaymentStatus } from "store/types";
import type { Binary } from "types";

import variables from "../../../assets/styles/_variables.module.scss";
import { resolveSlicePaymentDateImpactKind } from "./advanceSliceAmounts";

type SliceLockSource = {
    sliceId?: number | string | null;
    status?: PaymentStatus | null;
};

/**
 * React key for a slice row. The uuid `key` survives `setValue("slices", …)`, which regenerates RHF ids,
 * so both slice layouts (table and compact lines) remount rows under the same rule.
 */
export function advanceSliceRowKey(slice: { key?: string | number; id: string }) {
    return slice.key ?? slice.id;
}

export function getAdvanceSliceLock(
    slice: SliceLockSource,
    isUpdate: boolean,
    readOnly: boolean,
    isPeriodClosed: boolean,
) {
    const isExistingSlice = Boolean(slice.sliceId);
    const isPaidSlice = slice.status === PaymentStatus.PAID;
    const isDisabled = readOnly || isPeriodClosed || (isUpdate && isExistingSlice && isPaidSlice);
    return { isExistingSlice, isPaidSlice, isDisabled };
}

/** i18n key for a disabled slice field. Empty when the field stays editable. */
export function advanceSliceDisabledTooltipKey({
    readOnly,
    isPeriodClosed,
    isUpdate,
    isExistingSlice,
    isPaidSlice,
}: {
    readOnly: boolean;
    isPeriodClosed: boolean;
    isUpdate: boolean;
    isExistingSlice: boolean;
    isPaidSlice: boolean;
}): string | null {
    if (readOnly) return "BalanceUpdate.AdvancePage.OptionDisabledReadOnly";
    if (isPeriodClosed) return "BalanceUpdate.AdvancePage.OptionDisabled";
    if ((isUpdate || isExistingSlice) && isPaidSlice) return "BalanceUpdate.AdvancePage.OptionDisabledPaid";
    if (isExistingSlice) return "BalanceUpdate.AdvancePage.OptionDisabledUpdateNotPaid";
    return null;
}

/** A new slice dated before today is paid; a future date is to pay. Saved slices keep their status. */
export function paymentStatusForNewSliceDate(paymentDate: Date): PaymentStatus {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return paymentDate < today ? PaymentStatus.PAID : PaymentStatus.TO_PAY;
}

export function advanceSlicePeriodWarningKey({
    isDisabled,
    isForfait,
    paymentDate,
    periods,
    activePeriod,
}: {
    isDisabled: boolean;
    isForfait: Binary;
    paymentDate: Date | string | null | undefined;
    periods: TPeriod[];
    activePeriod?: TPeriod | null;
}): "AdvancePayment.PaymentDateOutsideActivePeriod" | "AdvancePayment.PaymentDateNoPeriodImpact" | null {
    if (isDisabled || isForfait) return null;
    const impactKind = resolveSlicePaymentDateImpactKind(paymentDate, periods, activePeriod);
    if (impactKind === "future") return "AdvancePayment.PaymentDateOutsideActivePeriod";
    if (impactKind === "none") return "AdvancePayment.PaymentDateNoPeriodImpact";
    return null;
}

export function getSlicePaymentStatusStyles(status: PaymentStatus) {
    switch (status) {
        case PaymentStatus.PAID:
            return {
                color: variables.success,
                backgroundColor: alpha(variables.green, 0.4),
            };
        case PaymentStatus.TO_PAY:
            return {
                color: variables["orange-opaque-text"],
                backgroundColor: variables["orange-chip"],
            };
        default:
            return {
                color: variables["dark-red"],
                backgroundColor: alpha(variables.red, 0.4),
            };
    }
}
